import React, { useState, useMemo } from 'react';
import { FaSearch, FaStar, FaNetworkWired, FaChevronLeft, FaChevronRight, FaInfoCircle } from 'react-icons/fa';
import { devices } from '../data/devices';
import { allTraccarDevices, getDevicesByProtocol, getDeviceStats } from '../data/allDevices';
import './UnifiedDeviceList.css';

const UnifiedDeviceList = ({ onFeaturedClick, onDeviceSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProtocol, setSelectedProtocol] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllDevices, setShowAllDevices] = useState(false);
  const itemsPerPage = 12;

  const devicesByProtocol = useMemo(() => getDevicesByProtocol(), []);
  const stats = useMemo(() => getDeviceStats(), []);

  // Filtragem
  const filteredDevices = useMemo(() => {
    let deviceslist = allTraccarDevices;

    if (selectedProtocol !== 'all') {
      deviceslist = deviceslist.filter(d => d.protocol === selectedProtocol);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      deviceslist = deviceslist.filter(d =>
        d.name.toLowerCase().includes(term) ||
        d.protocol.toLowerCase().includes(term) ||
        d.category.toLowerCase().includes(term)
      );
    }

    return deviceslist;
  }, [searchTerm, selectedProtocol]);

  // Paginação
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);
  const paginatedDevices = filteredDevices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleProtocolChange = (protocol) => {
    setSelectedProtocol(protocol);
    setCurrentPage(1);
    setShowAllDevices(true);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    if (e.target.value) {
      setShowAllDevices(true);
    }
  };

  return (
    <section id="devices" className="unified-device-list">
      {/* Estatísticas */}
      <div className="stats-container">
        <div className="stat-box">
          <div className="stat-icon">📱</div>
          <div className="stat-content">
            <span className="stat-number">{stats.totalDevices}+</span>
            <span className="stat-label">Dispositivos</span>
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-icon">🔌</div>
          <div className="stat-content">
            <span className="stat-number">{stats.totalProtocols}+</span>
            <span className="stat-label">Protocolos</span>
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-icon">🌟</div>
          <div className="stat-content">
            <span className="stat-number">{devices.length}</span>
            <span className="stat-label">Em Destaque</span>
          </div>
        </div>
      </div>

      {/* Cabeçalho */}
      <div className="catalog-header">
        <h2 className="catalog-title">Catálogo Completo de Dispositivos</h2>
        <p className="catalog-subtitle">
          Explore nossa seleção completa com mais de {stats.totalDevices} dispositivos GPS compatíveis com Traccar
        </p>
      </div>

      {/* Busca */}
      <div className="search-wrapper">
        <div className="search-box-main">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar dispositivo, protocolo ou categoria..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input-main"
          />
          {searchTerm && (
            <button 
              className="search-clear"
              onClick={() => {
                setSearchTerm('');
                setShowAllDevices(false);
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Dispositivos em Destaque */}
      {!searchTerm && !showAllDevices && (
        <div className="featured-section">
          <div className="featured-header">
            <h3>
              <FaStar className="star-icon" />
              Dispositivos em Destaque
            </h3>
            <p>Modelos mais populares com documentação completa</p>
          </div>
          
          <div className="featured-grid">
            {devices.map(device => (
              <div
                key={device.id}
                className="featured-card"
                onClick={() => onFeaturedClick(device)}
              >
                <div className="featured-badge">
                  <FaStar /> Destaque
                </div>
                <div className="featured-card-header">
                  <div className="featured-protocol">
                    {device.protocol}
                  </div>
                  <div className="featured-port">
                    <FaNetworkWired /> {device.port}
                  </div>
                </div>
                <h4 className="featured-name">{device.name}</h4>
                <p className="featured-category">{device.category}</p>
                <div className="featured-features">
                  {device.features.slice(0, 3).map((feature, idx) => (
                    <span key={idx} className="feature-tag-small">
                      {feature}
                    </span>
                  ))}
                </div>
                <button className="featured-btn">
                  <FaInfoCircle /> Ver Detalhes
                </button>
              </div>
            ))}
          </div>

          <div className="view-all-container">
            <button 
              className="view-all-btn"
              onClick={() => setShowAllDevices(true)}
            >
              Ver Todos os {stats.totalDevices}+ Dispositivos
            </button>
          </div>
        </div>
      )}

      {/* Catálogo Completo */}
      {(showAllDevices || searchTerm) && (
        <div className="catalog-section">
          <div className="catalog-controls">
            <h3 className="catalog-section-title">
              {searchTerm ? `Resultados da Busca (${filteredDevices.length})` : 'Todos os Dispositivos'}
            </h3>
            
            {!searchTerm && (
              <button 
                className="back-to-featured"
                onClick={() => setShowAllDevices(false)}
              >
                ← Voltar aos Destaques
              </button>
            )}
          </div>

          {/* Filtros por Protocolo */}
          <div className="protocols-filter">
            <div className="protocol-tabs-scroll">
              <button
                className={`protocol-btn ${selectedProtocol === 'all' ? 'active' : ''}`}
                onClick={() => handleProtocolChange('all')}
              >
                <span className="protocol-label">Todos</span>
                <span className="protocol-badge">{allTraccarDevices.length}</span>
              </button>
              
              {devicesByProtocol.slice(0, 15).map(({ protocol, port, devices: protoDevices }) => (
                <button
                  key={protocol}
                  className={`protocol-btn ${selectedProtocol === protocol ? 'active' : ''}`}
                  onClick={() => handleProtocolChange(protocol)}
                >
                  <span className="protocol-label">{protocol}</span>
                  <span className="protocol-port-info">:{port}</span>
                  <span className="protocol-badge">{protoDevices.length}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Grid de Dispositivos */}
          <div className="catalog-grid">
            {paginatedDevices.map((device, index) => (
              <div
                key={`${device.protocol}-${device.name}-${index}`}
                className="catalog-card"
                onClick={() => onDeviceSelect(device)}
              >
                <div className="catalog-card-top">
                  <span className="catalog-protocol">{device.protocol}</span>
                  <span className="catalog-port">
                    <FaNetworkWired /> {device.port}
                  </span>
                </div>
                <h5 className="catalog-device-name">{device.name}</h5>
                <p className="catalog-device-category">{device.category}</p>
                <div className="catalog-card-footer">
                  <span className="catalog-view">Ver detalhes →</span>
                </div>
              </div>
            ))}
          </div>

          {/* Sem resultados */}
          {filteredDevices.length === 0 && (
            <div className="no-results-box">
              <div className="no-results-icon">🔍</div>
              <h4>Nenhum dispositivo encontrado</h4>
              <p>Tente ajustar sua busca ou filtros</p>
              <button 
                onClick={() => { 
                  setSearchTerm(''); 
                  setSelectedProtocol('all');
                  setShowAllDevices(false);
                }}
                className="reset-btn"
              >
                Limpar Filtros
              </button>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FaChevronLeft /> Anterior
              </button>
              
              <div className="pagination-details">
                <span className="page-indicator">
                  Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                </span>
                <span className="items-indicator">
                  Exibindo {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredDevices.length)} de {filteredDevices.length}
                </span>
              </div>
              
              <button
                className="pagination-button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Próxima <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Referência Traccar */}
      <div className="traccar-footer">
        <p>
          Todos os dispositivos são compatíveis com{' '}
          <a href="https://www.traccar.org/devices/" target="_blank" rel="noopener noreferrer">
            Traccar - Sistema de Rastreamento GPS Open Source
          </a>
        </p>
      </div>
    </section>
  );
};

export default UnifiedDeviceList;

